import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import MissiveLandingHelmet from 'common/helmets/MissiveLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Missive1 from 'img/landing/missive-1.webp';
import MissiveWordmark from 'img/landing/missive-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const MissiveLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Missive Case Study');
  }, [trackEvent]);

  return (
    <div className="landingMissive landingCustomersPage">
      <MissiveLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Missive uses Canny to cut down on support time and prioritize new features" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/jObAltKurjY"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Missive is redefining email for SMBs (small & medium-sized businesses). At Canny,
              we’ve been using Missive for years and love collaborating through this app. Around
              2,500 businesses around the world do&nbsp;too!
            </p>
            <p>
              Philippe Lehoux and just six other team members at Missive make it all happen.
              Philippe admits that he’s doing a lot – sales, marketing, product development, coding,
              customer support, and more. Naturally, there’s a lot to do and very little time to do
              it&nbsp;all.
            </p>
            <div className="sectionTitle">Challenge</div>
            <p>
              An email client and collaboration tool like Missive gets tons of
              feature&nbsp;requests.
            </p>
            <p className="quote">
              &ldquo;There are probably millions of possible features. And each client has an
              opinion about what an email client should&nbsp;do.&rdquo;
            </p>
            <p>
              With such a large volume of feedback, tracking and managing it all was getting out
              of&nbsp;hand.
            </p>
            <p className="quote">
              &ldquo;Before Canny, we had a spreadsheet. For each request, we had an email, a name,
              and a link to that request. Once we shipped a feature, we had to manually go through
              the list and follow up with each person who requested this feature. It just
              wasn’t&nbsp;scalable.&rdquo;
            </p>
            <p>
              What happens when a prospective customer requests a deal-breaker feature? With no
              system in place, Missive was losing those customers. Remembering to follow up with
              them once the feature is built was a lot of&nbsp;work.
            </p>
            <p>Their time spent on customer support was also rapidly&nbsp;increasing.</p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Missive was looking for a way to automate these tasks and keep their
              customers&nbsp;happy.
            </p>
            <p className="quote">
              &ldquo;As soon as we came across Canny, it clicked for us. We were spending a lot of
              time simply replying to people: "Oh, it's a good idea, but we're not ready to do it."
              Canny really changed the game for us and reduced the amount of support we had
              to&nbsp;do.&rdquo;
            </p>
            <div className="imageWithCaption">
              <LazyLoadedImage className="border" src={Missive1} />
              <p className="caption">Canny is embedded into Missive's help & feedback menu</p>
            </div>
            <p>Philippe was a little hesitant to adopt Canny at&nbsp;first.</p>
            <p className="quote">
              &ldquo;I thought – if I redirect my users to submit a feature request, won’t they feel
              offended that I didn’t dedicate time to speak with them directly? But no, people are
              happy to suggest features in our community. They also engage in discussions among each
              other, and that really helps us understand their thought&nbsp;process.&rdquo;
            </p>
            <p>
              Now, Philippe and his team are using Canny every day. They direct their users to
              submit feature requests, comment, and upvote&nbsp;others.
            </p>
            <p>Missive also turns to Canny to guide their decision&nbsp;making.</p>
            <p className="quote">
              &ldquo;At one point I discovered that I could order feature requests by MRR. And that
              was quite&nbsp;amazing.&rdquo;
            </p>
            <p>
              This customization lets Missive prioritize the most impactful features and ultimately
              decide what to build&nbsp;next.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Philippe and his team at Missive see the value of Canny very clearly. They feel like
              they’d miss out on so many opportunities if they stopped using Canny&nbsp;today.
            </p>
            <p className="quote">
              &ldquo;We would have to either build the same thing ourselves or use something else
              (if it exists). But clearly, the value is there. Our customers love it. We’ve never
              had a single&nbsp;complaint.&rdquo;
            </p>
            <p>
              Missive is happy to report great improvements in productivity and a reduced amount of
              customer&nbsp;support.
            </p>
            <p className="quote">
              &ldquo;Canny really helps us not just with feedback management, but also with
              decision-making. It pushes us to be more creative with what we’re building next.
              Because, Canny helps us balance user ideas with our own&nbsp;input.&rdquo;
            </p>
            <p>
              Philippe strongly recommends Canny because it’s worry-free, simple, and just works. He
              notes its user-friendly design and how easy it was to set up. Most of all, Philippe
              loves all the time he’s saving with Canny’s&nbsp;help.
            </p>
            <p>
              We’re grateful for loyal customers like Missive and would love to have you join
              them&nbsp;too!
            </p>
            <p>Give Canny a&nbsp;try!</p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Missive Wordmark"
              className="missiveWordmark"
              src={MissiveWordmark}
            />
            Missive is the team inbox and chat app that empowers teams to truly collaborate
            around&nbsp;email.
            <a href="https://missiveapp.com/" rel="noopener" target="_blank">
              missiveapp.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>6</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2016</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2018</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>25,960+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default MissiveLanding;
